/**
 * El cubo: elegís filas y columnas, y sale la tabla.
 *
 * Los cortes estaban fijos —gerencia, prioridad, estado, categoría— y cada pregunta nueva era
 * una pantalla nueva. Acá las dimensiones **las trae el servidor** (`/pmo/dimensiones`): la
 * lista no está escrita en el frontend, así que agregar una dimensión no obliga a tocar dos
 * repos.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { analyticsAPI, formatApiError } from '@/lib/api';
import { BarraDeSemaforo, ChartCard, CustomTooltip, colorDeSerie } from '@/components/charts';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

const ETIQUETAS = {
  intent: 'Intent', categoria: 'Categoría', prioridad: 'Prioridad', estado: 'Estado',
  gerencia: 'Gerencia', tipo: 'Tipo', plantilla: 'Plantilla', label: 'Label', mes: 'Mes',
  responsable: 'Responsable', abierto: 'En curso / terminada', atendido: 'Atendida',
  reloj_detenido: 'Reloj',
};

/**
 * Altas manuales, con su denominador.
 *
 * El backend la manda como `{n, sobre}` a propósito: "12 altas manuales" no dice nada sin
 * saber si fue sobre 15 casos o sobre 1.500. La proporción es el dato; el número suelto
 * invita a leerlo mal en las dos direcciones.
 */
function fmtAltas(a) {
  if (!a || !a.sobre) return <span className="text-zinc-300">—</span>;
  const pct = Math.round((a.n / a.sobre) * 100);
  return <span title={`${a.n} de ${a.sobre}`}>{a.n} · {pct}%</span>;
}

/**
 * Conformidad, **nunca sin su `n`**.
 *
 * Es la regla que el propio cubo escribió al calcularla: un 100 % sobre una respuesta y un
 * 100 % sobre ochenta son el mismo número con distinto significado, y quien mira un tablero
 * no tiene cómo distinguirlos si el `n` no viaja al lado.
 */
function fmtConformidad(c) {
  if (!c || c.satisfaccion_p50 == null) {
    return <span className="text-zinc-300" title="nadie contestó todavía">—</span>;
  }
  return (
    <span title={`${c.respondidas} respuesta${c.respondidas === 1 ? '' : 's'}`}>
      {c.satisfaccion_p50}
      <span className="text-zinc-400"> (n={c.respondidas})</span>
    </span>
  );
}

export default function PanelAnalisis({ params, unidadPlural = 'Tickets' }) {
  const [dimensiones, setDimensiones] = useState([]);
  const [rows, setRows] = useState('gerencia');
  const [cols, setCols] = useState('');
  const [cubo, setCubo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    analyticsAPI.pmoDimensiones()
      .then((r) => setDimensiones(r.data?.dimensiones || []))
      .catch(() => setDimensiones([]));
  }, []);

  // El padre pasa `params={analyticsParams()}`: un objeto **nuevo en cada render**. Con él
  // como dependencia, `cargar` se recreaba siempre, el efecto se volvía a disparar y cada
  // respuesta provocaba el render siguiente — un bucle. Abrir la pestaña disparaba seis
  // consultas al cubo sin que nadie tocara nada. Se depende del contenido, no de la identidad.
  const paramsKey = JSON.stringify(params || {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramsEstables = useMemo(() => params, [paramsKey]);

  //: El número de la última consulta pedida. Sin esto, una respuesta lenta de una consulta
  //: vieja pisa a la nueva y la pantalla muestra **el resultado de otra pregunta**, en
  //: silencio: elegís el cruce, la tabla no cambia y parece que el cruce no anda. Aparecía
  //: contra el VPS —más lento que local— y no contra la máquina de uno, que es el peor lugar
  //: donde puede esconderse un bug.
  const ultimaConsulta = useRef(0);

  const cargar = useCallback(async () => {
    const consulta = ++ultimaConsulta.current;
    setLoading(true);
    try {
      const r = await analyticsAPI.pmoCubo({ ...paramsEstables, rows, cols: cols || undefined });
      if (consulta !== ultimaConsulta.current) return;
      setCubo(r.data);
    } catch (e) {
      if (consulta !== ultimaConsulta.current) return;
      // El backend rechaza una dimensión inventada con la lista de válidas: se muestra tal
      // cual en vez de un "error al cargar" que no dice nada.
      setCubo(null);
      toast.error(formatApiError(e));
    } finally {
      if (consulta === ultimaConsulta.current) setLoading(false);
    }
  }, [paramsEstables, rows, cols]);

  useEffect(() => { cargar(); }, [cargar]);

  const filas = cubo?.filas || [];
  const datosGrafico = filas
    .filter((f) => !f.es_bucket)
    .slice(0, 12)
    .map((f) => ({ nombre: f.clave, unidades: f.total.unidades }));

  return (
    <div className="space-y-4">
      <Card className="rounded-sm border-brand-border">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          {[['Filas', rows, setRows, false], ['Columnas', cols, setCols, true]].map(
            ([label, valor, set, opcional]) => (
              <div key={label}>
                <label className="text-[10px] text-zinc-500 block">{label}</label>
                <select
                  className="border border-brand-border rounded-sm h-8 px-2 text-xs bg-white min-w-[10rem]"
                  value={valor}
                  onChange={(e) => set(e.target.value)}
                  data-testid={`cubo-${label.toLowerCase()}`}
                >
                  {opcional && <option value="">(ninguna)</option>}
                  {dimensiones.map((d) => (
                    <option key={d} value={d}>{ETIQUETAS[d] || d}</option>
                  ))}
                </select>
              </div>
            )
          )}
          <Button size="sm" variant="outline" className="text-xs h-8 rounded-sm" onClick={cargar} disabled={loading}>
            <RefreshCw className="w-3 h-3 mr-1" /> Actualizar
          </Button>
          {cubo?.costo && (
            <span className="text-[10px] text-zinc-500 ml-auto">
              costo atribuido US$ {cubo.costo.atribuido} de US$ {cubo.costo.total}
              <span className="text-zinc-400"> · US$ {cubo.costo.no_atribuido} sin unidad de trabajo</span>
            </span>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
      ) : !filas.length ? (
        <p className="text-[11px] text-zinc-400 p-3">Sin datos para este corte.</p>
      ) : (
        <>
          <ChartCard title={`${unidadPlural} por ${ETIQUETAS[rows] || rows}`} data-testid="cubo-grafico">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={datosGrafico} margin={{ top: 8, right: 8, left: -18, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="nombre" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F4F4F5' }} />
                <Bar dataKey="unidades" name={unidadPlural} radius={[2, 2, 0, 0]}>
                  {datosGrafico.map((_, i) => <Cell key={i} fill={colorDeSerie(i)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <Card className="rounded-sm border-brand-border">
            <CardHeader className="pb-2 border-b border-brand-border py-2">
              <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">
                {ETIQUETAS[rows] || rows}{cols ? ` × ${ETIQUETAS[cols] || cols}` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[28rem] overflow-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-zinc-50 border-b border-brand-border sticky top-0">
                    <tr>
                      <th className="text-left p-2">{ETIQUETAS[rows] || rows}</th>
                      <th className="text-right p-2 w-20">Unidades</th>
                      <th className="text-right p-2 w-20">En curso</th>
                      <th className="text-left p-2 w-40">SLA resolución</th>
                      <th className="text-right p-2 w-24">Cumplimiento</th>
                      <th className="text-right p-2 w-24">p50 atención</th>
                      <th className="text-right p-2 w-24" title="Veces que un caso cerrado volvió a abrirse">Reaperturas</th>
                      {/* Las tres señales de la interrupción se calculaban desde el 2026-08-26
                          y no se pintaba ninguna: sólo se veían mirando el JSON de la
                          respuesta. Medir algo que nadie puede mirar es no medirlo. */}
                      <th className="text-right p-2 w-24" title="Cambios de dueño reales, sin contar la asignación inicial">Traspasos</th>
                      <th className="text-right p-2 w-24" title="Veces que alguien cambió la prioridad o la severidad a mano">Repriorizac.</th>
                      <th className="text-right p-2 w-28" title="Casos cargados a mano, fuera del circuito de los canales">Altas manuales</th>
                      <th className="text-right p-2 w-28" title="Mediana de conformidad de quien pidió, con cuántos contestaron">Conformidad</th>
                      {(cubo.columnas || []).map((c) => (
                        <th key={c} className="text-right p-2 w-20">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f) => (
                      <tr key={f.clave} className={`border-b border-zinc-100 ${f.es_bucket ? 'bg-zinc-50 italic text-zinc-500' : ''}`}>
                        <td className="p-2">{f.clave}</td>
                        <td className="p-2 text-right font-semibold">{f.total.unidades}</td>
                        <td className="p-2 text-right">{f.total.abiertas}</td>
                        <td className="p-2"><BarraDeSemaforo sla={f.total.sla_resolucion} /></td>
                        <td className="p-2 text-right">
                          {f.total.sla_resolucion?.cumplimiento_pct == null
                            /* `—` y no `0%`: no es que se incumplió todo, es que no había
                               nada con compromiso contra qué medir. */
                            ? <span className="text-zinc-300" title="nada con reloj">—</span>
                            : `${f.total.sla_resolucion.cumplimiento_pct}%`}
                        </td>
                        <td className="p-2 text-right">
                          {f.total.horas_a_atencion_p50 == null ? '—' : `${f.total.horas_a_atencion_p50}h`}
                        </td>
                        <td className="p-2 text-right">{f.total.reaperturas || 0}</td>
                        <td className="p-2 text-right">{f.total.traspasos || 0}</td>
                        <td className="p-2 text-right">{f.total.repriorizaciones || 0}</td>
                        <td className="p-2 text-right">{fmtAltas(f.total.altas_manuales)}</td>
                        <td className="p-2 text-right">{fmtConformidad(f.total.conformidad)}</td>
                        {(cubo.columnas || []).map((c) => (
                          <td key={c} className="p-2 text-right text-zinc-500">
                            {f.celdas?.[c]?.unidades ?? '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-zinc-50 border-t-2 border-brand-border sticky bottom-0">
                    {/* Los totales los calcula el backend sobre el universo: el cumplimiento
                        y la mediana no son aditivos, y sumarlos acá mentiría. */}
                    <tr className="font-semibold">
                      <td className="p-2">Total</td>
                      <td className="p-2 text-right">{cubo.totales?.unidades}</td>
                      <td className="p-2 text-right">{cubo.totales?.abiertas}</td>
                      <td className="p-2"><BarraDeSemaforo sla={cubo.totales?.sla_resolucion} /></td>
                      <td className="p-2 text-right">
                        {cubo.totales?.sla_resolucion?.cumplimiento_pct == null
                          ? '—' : `${cubo.totales.sla_resolucion.cumplimiento_pct}%`}
                      </td>
                      <td className="p-2 text-right">
                        {cubo.totales?.horas_a_atencion_p50 == null ? '—' : `${cubo.totales.horas_a_atencion_p50}h`}
                      </td>
                      <td className="p-2 text-right">{cubo.totales?.reaperturas || 0}</td>
                      <td className="p-2 text-right">{cubo.totales?.traspasos || 0}</td>
                      <td className="p-2 text-right">{cubo.totales?.repriorizaciones || 0}</td>
                      <td className="p-2 text-right">{fmtAltas(cubo.totales?.altas_manuales)}</td>
                      <td className="p-2 text-right">{fmtConformidad(cubo.totales?.conformidad)}</td>
                      {(cubo.columnas || []).map((c) => <td key={c} />)}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
